import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  CalendarOff, ChevronLeft, Clock, Coffee, LogOut, Loader2, 
  Plus, CheckCircle, XCircle, Clock3, AlertCircle, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

/**
 * Staff Time Off Page
 * Allows staff to request and view time off requests
 */
export default function StaffTimeOffPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [staffInfo, setStaffInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [requestType, setRequestType] = useState('pto'); // pto, sick, personal

  const token = localStorage.getItem('staff_token');

  useEffect(() => {
    if (!token) {
      navigate('/staff/login');
      return;
    }
    fetchStaffInfo();
    fetchRequests();
  }, [token, navigate]);

  const fetchStaffInfo = async () => {
    try {
      const res = await api.get(`/staff-portal/profile?token=${token}`);
      setStaffInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch staff info:', err);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/staff-portal/time-off-requests?token=${token}`);
      setRequests(res.data.requests || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('staff_token');
        navigate('/staff/login');
      }
      console.error('Failed to fetch requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/staff-portal/time-off-request?token=${token}`, {
        start_date: startDate,
        end_date: endDate,
        request_type: requestType,
        reason: reason
      });
      
      toast.success('Time off request submitted!');
      setShowForm(false);
      setStartDate('');
      setEndDate('');
      setReason('');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_info');
    toast.success('Logged out');
    navigate('/staff/login');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'denied':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock3 className="w-5 h-5 text-amber-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'denied':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff/profile" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">Time Off Requests</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{staffInfo?.name || 'Staff'}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* New Request Button */}
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="w-full mb-6 bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request Time Off
          </Button>
        )}

        {/* Request Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">New Time Off Request</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Request Type
                </label>
                <select
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="pto">Paid Time Off (PTO)</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Day</option>
                  <option value="unpaid">Unpaid Leave</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || new Date().toISOString().split('T')[0]}
                    className="bg-white dark:bg-gray-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Brief description of why you need time off..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !startDate || !endDate}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Requests List */}
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Requests</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <CalendarOff className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No time off requests yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Click the button above to request time off
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request, index) => (
              <div 
                key={request.id || index}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getStatusIcon(request.status)}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 dark:text-white capitalize">
                          {request.request_type?.replace('_', ' ') || 'Time Off'}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getStatusBadge(request.status)}`}>
                          {request.status || 'pending'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        {formatDate(request.start_date)} - {formatDate(request.end_date)}
                      </p>
                      {request.reason && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          &quot;{request.reason}&quot;
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {request.manager_note && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Manager Note:</strong> {request.manager_note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-700 dark:text-blue-400">
              Please submit time off requests at least 2 weeks in advance when possible. Emergency requests will be reviewed on a case-by-case basis.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
